import React, { useState } from 'react'
import gql from 'graphql-tag'
import { useDispatch } from 'react-redux'
import { useQuery, useMutation } from 'react-apollo'
import * as Yup from 'yup'
import { Formik, Field, FormikProps } from 'formik'

import { formUrl } from 'utils/url'
import { TextField } from 'components/common/formik'
import { CenteredSpinner } from 'components/common/loading'
import history from 'utils/history'
import { PDF_CONTENT_TYPE } from 'utils/constants'
import { useGlobalContext } from 'components/context'
import { createNotification } from 'state/actions/userInterface'
import EmailSelect from './email-select'
import { email_Options } from './gql-types/email_Options'
import { sendEmails, sendEmailsVariables } from './gql-types/sendEmails'
import { FormValues } from './types'
import * as styles from './index.module.sass'


const PUBLIC_FORM_EMAIL_TYPE = "PF"
const PDF_EMAIL_TYPE = "PDF"

const GET_EMAIL_OPTIONS = gql`
	query email_Options {
		emailOption_List {
			objId
			email
			sourceType
			name
			detail
			recordWord
		}
	}
`

const SEND = gql`
	mutation sendEmails(
		$typeString: String!,
		$createdBy: ID!,
		$userSuppliedContext: String!,
		$sendToEmail: String!,
		$sendToContentType: String,
		$sendToObjectId: ID,
		$regardingContentType: String!,
		$regardingObjectId: ID!,
	) {
		email_Update(
			input: {
				typeString: $typeString,
				createdBy: $createdBy,
				userSuppliedContext: $userSuppliedContext,
				sendToEmail: $sendToEmail,
				sendToContentType: $sendToContentType,
				sendToObjectId: $sendToObjectId,
				regardingContentType: $regardingContentType,
				regardingObjectId: $regardingObjectId,
			}
		) {
			object {
				id
			}
			errors {
				field
				messages
			}
		}
	}
`

type CustomOption = string


type Props = {
	recordId: string
	formId: string
	regardingObjectId: string
	regardingObjectContentType: string
}


const sendEmailModal = ({recordId, formId, regardingObjectId, regardingObjectContentType}: Props) => {
	const [showAddEmail, setShowAddEmail] = useState(true)
	const globalContext = useGlobalContext()
	const { data, loading } = useQuery<email_Options>(GET_EMAIL_OPTIONS, { variables: { offset: 0 }})
	const [mutate, { loading: mutating }] = useMutation<sendEmails, sendEmailsVariables>(SEND, {refetchQueries: ['email_Options']})
	const dispatch = useDispatch()
	
	const postSend = () => {
		setShowAddEmail(true)
		dispatch(createNotification({
			'content': `Email(s) sent successfully`
		}))
		history.push(formUrl({recordId, formId, ...globalContext}))
	};

	const renderFormInner = ({ handleSubmit, values }: FormikProps<FormValues>) => {
		const emails = [
			...values.emailOptions.filter(o => o.selected).map(o => o.email),
			...values.newCustomEmails.filter((ce: CustomOption) => ce !== ''),
		]
		return (
			<div>
				<div className={styles.sendingToContainer}>
					<div className={styles.sendingToTitle}>Sending to:</div>
					<div className={styles.sendingToList}>
						{emails.map((e, index) => (
							<span key={index} className={styles.emailItem}>
								{e}
							</span>
						))}
						<button
							className="button-no-outline"
							onClick={() => setShowAddEmail(true)}
						>
							<span className="icon-pencil"/>
						</button>
					</div>
				</div>
				<br/>
				<div className={styles.subtitle}>Email message (optional):</div>
				<Field
					placeholder={""}
					name="context"
					component={TextField}
					textarea={true}
				/>
				Note: You will not receive the replies to this email
				<div className={styles.buttonContainer}>
					<button
						type="button"
						className="button-primary-solid"
						//@ts-ignore
						onClick={handleSubmit}
						disabled={mutating}
					>
						Send
					</button>
				</div>
			</div>
		)
	}
	return (!data || mutating)
		? <CenteredSpinner/>
		: (
			<div>
				<Formik
					enableReinitialize
					initialValues={{
						context: "",
						newCustomEmails: [] as string[],
						emailOptions: data?.emailOption_List?.map(
							option => ({...option, selected: false})
						) as FormValues["emailOptions"],
					}}
					validationSchema={Yup.object().shape({
						context: Yup.string(),
						newCustomEmails: Yup.array().of(Yup.string().email()),
						emailOptions: Yup.array(Yup.object()),
					})}
					onSubmit={(values, {resetForm}) => {
						const emailData = {
							typeString: regardingObjectContentType == PDF_CONTENT_TYPE ? PDF_EMAIL_TYPE : PUBLIC_FORM_EMAIL_TYPE,
							createdBy: globalContext.user.id,
							userSuppliedContext: values.context,
							regardingContentType: regardingObjectContentType,
							regardingObjectId: regardingObjectId,
						}

						const answers = values.emailOptions.filter(ae => ae.selected)
						const emailOptions = answers.map(a => ({
							...emailData,
							sendToContentType: a.sourceType,
							sendToObjectId: a.objId,
							sendToEmail: a.email,
						}))
						
						const newCustomEmails = values.newCustomEmails.filter(ce => ce != '').map(ce => ({
							...emailData,
							sendToContentType: null,
							sendToObjectId: null,
							sendToEmail: ce,
						}))
						const toSend = [
							...emailOptions,
							...newCustomEmails,
						]
						const promises = toSend.map(e => mutate({ variables: e }))
						Promise.all(promises).then(() => {
							resetForm()
							postSend()
						})
					}}
				>
					{(formProps) => {
						return showAddEmail
							? <EmailSelect
									toggleState={() => setShowAddEmail(!showAddEmail)}
									formProps={formProps}
								/>
							: renderFormInner(formProps)
					}}
				</Formik>
			</div>
		)
}

export default sendEmailModal